import { useEffect, useMemo, useState } from 'react';
import { Table as AntTable, Button } from 'antd';
import type { TableProps } from 'antd';
import { ApartmentOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import type { IComponentSchema } from '../../types';
import { encodeUrlParams, parseRuntimeTemplateString } from '../../utils';
import { searchJmesPath } from '../../../utils';
import { mapToTableColumns } from '../../utils/mapToTableColumns';
import { usePage } from '../../context/PageContext';
import useTableQueryParams from '../../../hooks/useTableQueryParams';
import useNormalizeColumns from '../table/useColumnsNormilizer';
import type { ITreeTableProps } from './helper';
import { useTreeTableTree, type ITreeTableRow } from './useTreeTableTree';

interface IProps {
  schema: IComponentSchema<ITreeTableProps>;
  parentContext?: any;
}

const TreeTable = ({ schema, parentContext }: IProps) => {
  const props = (schema['x-component-props'] || {}) as ITreeTableProps;
  const {
    visible,
    columns = [],
    data,
    dataTransformer,
    navigateOnRowClick,
    pagination,
    loading,
    defaultView = 'hierarchical',
    showViewToggle = true,
    modelIds: modelIdsProp,
    rootFilter,
    childKey,
    parentKey,
    hasChildrenKey,
    childrenPageSize,
    rowItemName = 'row',
    prefix,
    onQueryChange,
  } = props;

  const pageProps = usePage();
  const navigate = useNavigate();

  const { page, size, sortName, sortOrder, handleChange } = useTableQueryParams(
    {
      prefix,
      initialParams: { size: pagination?.pageSize },
    }
  );

  const [view, setView] = useState<'flat' | 'hierarchical'>(defaultView);
  const isHierarchical = view === 'hierarchical';

  const context = useMemo(() => {
    return {
      ...pageProps.context,
      ...(parentContext || {}),
    };
  }, [pageProps.context, parentContext]);

  // Flat view is driven by the ListPage (same as the Table component); the
  // hierarchical view self-fetches, so skip the ListPage pagination sync there.
  useEffect(() => {
    if (!isHierarchical) {
      const handler =
        typeof onQueryChange === 'function'
          ? onQueryChange
          : pageProps.onQueryChange;
      handler?.({ page: page ?? 1, size: size ?? 10, sortName, sortOrder });
    }
  }, [page, size, sortName, sortOrder, isHierarchical]);

  const modelIds = modelIdsProp ?? pageProps.modelIds;
  const scopeFilter = pageProps.dataFilters;

  const {
    treeData,
    total: rootsTotal,
    isLoading: isTreeLoading,
    loadChildren,
  } = useTreeTableTree({
    enabled: isHierarchical,
    modelIds,
    scopeFilter,
    rootFilter,
    childKey,
    parentKey,
    hasChildrenKey,
    childrenPageSize,
    page: page ?? 1,
    size: size ?? 10,
  });

  const flatData = useMemo(() => {
    const safe = Array.isArray(data) ? data : [];
    return dataTransformer && dataTransformer.trim() !== 'Condition Logic'
      ? searchJmesPath(safe, dataTransformer)
      : safe;
  }, [data, dataTransformer]);

  const columnsArray = useNormalizeColumns(columns);
  const visibleColumns = columnsArray.filter(col => col.visible !== false);
  const transformedColumns = useMemo(
    () =>
      mapToTableColumns({
        columns: visibleColumns,
        context: context,
        rowName: rowItemName,
      }),
    [visibleColumns, context, rowItemName]
  );

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  useEffect(() => {
    setExpandedKeys([]);
  }, [view, page, size]);

  const handleExpand = (expanded: boolean, record: ITreeTableRow) => {
    setExpandedKeys(keys =>
      expanded ? [...keys, record.id] : keys.filter(k => k !== record.id)
    );
    if (expanded) loadChildren(record);
  };

  const handleRowClick = (record: any) => {
    if (!navigateOnRowClick || record?._placeholder) return;
    const url = parseRuntimeTemplateString(navigateOnRowClick.trim(), {
      [rowItemName]: record,
    });
    if (url) navigate(encodeUrlParams(url));
  };

  if (visible === false) return null;

  const total = isHierarchical ? rootsTotal : (pagination?.total ?? 0);

  const paginationConfig = pagination?.show
    ? {
        showSizeChanger: pagination?.showSizeChanger,
        position: pagination?.position,
        current: page,
        pageSize: size ?? undefined,
        total,
      }
    : (false as const);

  const expandable: TableProps<ITreeTableRow>['expandable'] = isHierarchical
    ? {
        childrenColumnName: 'children',
        expandedRowKeys: expandedKeys,
        onExpand: handleExpand,
      }
    : undefined;

  return (
    <Wrapper>
      {showViewToggle && (
        <ToggleBar>
          <Button
            type="link"
            icon={
              isHierarchical ? <UnorderedListOutlined /> : <ApartmentOutlined />
            }
            onClick={() =>
              setView(v => (v === 'flat' ? 'hierarchical' : 'flat'))
            }
          >
            {isHierarchical ? 'Flat view' : 'Hierarchical view'}
          </Button>
        </ToggleBar>
      )}
      <AntTable<ITreeTableRow>
        rowKey="id"
        columns={transformedColumns as TableProps<ITreeTableRow>['columns']}
        dataSource={isHierarchical ? treeData : flatData}
        loading={isHierarchical ? isTreeLoading : (loading as boolean)}
        pagination={paginationConfig}
        onChange={handleChange}
        expandable={expandable}
        rowClassName={navigateOnRowClick ? 'clickable-row' : undefined}
        onRow={record => ({
          onClick: e => {
            // Ignore clicks on the (default antd) expand icon so expanding a
            // row doesn't also navigate.
            if (
              (e.target as HTMLElement).closest('.ant-table-row-expand-icon')
            ) {
              return;
            }
            handleRowClick(record);
          },
        })}
      />
    </Wrapper>
  );
};

export default TreeTable;

const Wrapper = styled.div`
  .clickable-row {
    cursor: pointer;
  }
`;

const ToggleBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;
